import DataBlock from "./DataBlock";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useState, useEffect, memo, lazy, Suspense, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { getMediaBaseUrl, getMediaBaseUrlSync } from "../utils/media";

/** Rewrite a media URL to the same-origin `/api/media` proxy.
 *
 * The page bodies stored in the DB contain absolute CDN URLs
 * (e.g. https://media.cortex.joveo.ai/<s3_key>). Those load fine in
 * the browser but produce CORS-tainted canvases when `modern-screenshot`
 * tries to inline them during PDF capture, leaving white blocks where
 * images should be. The backend already proxies the same bytes through
 * `/api/media/{s3_key}` on the docs origin; swapping to that path
 * yields a same-origin fetch and a clean canvas.
 *
 * No-op when the URL already points at the API proxy or isn't a CDN
 * URL we recognise. */
function rewriteToProxyMediaUrl(src: string | undefined): string | undefined {
  if (!src) return src;
  const cdnBase = getMediaBaseUrlSync();
  // In dev the configured base is already `/api/media`, so nothing to rewrite.
  if (!cdnBase.startsWith("http")) return src;
  if (!src.startsWith(cdnBase + "/")) return src;
  return "/api/media" + src.slice(cdnBase.length);
}
import { parseImageAlt, imageWidthStyle } from "../utils/imageAlt";
import MermaidBlock from "./MermaidBlock";
import { useOptionalCms } from "../hooks/useCms";

// Lazy-loaded so react-syntax-highlighter (Prism + grammars) stays out of the
// main bundle; until it loads, the code shows as a plain <pre> (see fallback).
const CodeHighlighter = lazy(() => import("./CodeHighlighter"));

interface MarkdownRendererProps {
  content: string | null;
  className?: string;
  /** Map of filename → action ("added" | "removed" | "modified") */
  changedAssets?: Map<string, string>;
  /** Render print-friendly substitutes for non-printable elements
   * (currently <video>). Used by the PDF export surface — `<video>`
   * elements can't be captured to canvas in a meaningful way, so we
   * render a static placeholder card with the poster image (if any),
   * a play-button glyph, and the source URL as a caption. */
  printMode?: boolean;
  /** Intercept clicks on internal (relative) links. Return true if handled
   * (navigation done by the caller); false to fall through to default behavior.
   * Used by embeds without a CmsProvider (e.g. Enable docs) to route links to
   * their own page state instead of a hard browser navigation. */
  onNavigate?: (href: string) => boolean;
}

/** Generate a slug from heading text for anchor links. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");
}

/** Extract plain text from React children. */
function childrenToText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return childrenToText((children as any).props.children);
  }
  return "";
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="absolute top-2.5 right-2.5 px-2 py-1 text-[11px] font-medium rounded-md
        bg-white/80 text-slate-500 hover:text-slate-700 hover:bg-white
        border border-slate-200/60 backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function MarkdownRendererInner({ content, className = "", changedAssets, printMode = false, onNavigate }: MarkdownRendererProps) {
  if (!content) return null;

  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxInteractive, setLightboxInteractive] = useState(false);
  const navigate = useNavigate();
  const cms = useOptionalCms();
  const navigateToPath = cms?.navigateToPath;
  const activeSection = cms?.activeSection;

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightboxSrc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setLightboxSrc(null); setLightboxInteractive(false); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [lightboxSrc]);
  useEffect(() => { getMediaBaseUrl(); }, []);
  // In print/PDF mode, warm the syntax-highlighter chunk up front so code
  // blocks render highlighted (not the plain-<pre> fallback) before the
  // user-triggered DOM capture runs — no fallback flash in the captured PDF.
  useEffect(() => {
    if (printMode) void import("./CodeHighlighter");
  }, [printMode]);

  return (
    <>
    {lightboxSrc && (
      <div
        className="fixed inset-0 z-[9999] bg-black/80 overflow-auto"
        style={{ cursor: "grab" }}
        onClick={(e) => { if (e.target === e.currentTarget) { setLightboxSrc(null); setLightboxInteractive(false); } }}
        onMouseDown={(e) => {
          const el = e.currentTarget;
          el.style.cursor = "grabbing";
          const startX = e.clientX, startY = e.clientY;
          const scrollLeft = el.scrollLeft, scrollTop = el.scrollTop;
          const onMove = (ev: MouseEvent) => {
            el.scrollLeft = scrollLeft - (ev.clientX - startX);
            el.scrollTop = scrollTop - (ev.clientY - startY);
          };
          const onUp = () => {
            el.style.cursor = "grab";
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
          };
          document.addEventListener("mousemove", onMove);
          document.addEventListener("mouseup", onUp);
        }}
      >
        <div className="min-h-full min-w-full flex items-center justify-center p-8">
          {lightboxInteractive ? (
            <object
              type="image/svg+xml"
              data={lightboxSrc}
              className="max-w-none rounded-lg shadow-2xl bg-white"
              style={{ width: "90vw", maxHeight: "90vh" }}
            >
              <img src={lightboxSrc} alt="" className="max-w-none" />
            </object>
          ) : (
            <img
              src={lightboxSrc}
              alt=""
              className="max-w-none rounded-lg shadow-2xl select-none pointer-events-none"
              draggable={false}
            />
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); setLightboxInteractive(false); }}
          className="fixed top-4 right-4 z-[10000] text-white hover:text-white text-xl font-light leading-none size-9 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/20 transition-colors shadow-lg"
          aria-label="Close"
        >×</button>
      </div>
    )}
    <div className={`cortex-docs ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          code({ className: cn, children, ...props }) {
            const match = /language-([\w-]+)/.exec(cn || "");
            const codeStr = String(children).replace(/\n$/, "");
            if (match) {
              if (match[1] === "cortex-data") {
                return <DataBlock raw={codeStr} live={!printMode} renderFallback={(md) => <MarkdownRendererInner content={md} printMode={printMode} />} />;
              }
              // Mermaid diagrams — render as SVG instead of syntax-highlighted code
              if (match[1] === "mermaid") {
                return <MermaidBlock source={codeStr} />;
              }
              return (
                <div className="cortex-codeblock group">
                  <div className="cortex-codeblock-header">
                    <span>{match[1]}</span>
                  </div>
                  <CopyButton text={codeStr} />
                  <Suspense
                    fallback={
                      <pre
                        className="cortex-codeblock-fallback"
                        style={{
                          margin: 0,
                          padding: "1rem 1.25rem",
                          background: "#fafbfc",
                          fontSize: "0.8125rem",
                          lineHeight: "1.7",
                          borderRadius: "0 0 0.5rem 0.5rem",
                          overflowX: "auto",
                          fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                        }}
                      >
                        <code>{codeStr}</code>
                      </pre>
                    }
                  >
                    <CodeHighlighter
                      theme="light"
                      language={match[1]}
                      PreTag="div"
                      code={codeStr}
                      customStyle={{
                        margin: 0,
                        padding: "1rem 1.25rem",
                        background: "#fafbfc",
                        fontSize: "0.8125rem",
                        lineHeight: "1.7",
                        borderRadius: "0 0 0.5rem 0.5rem",
                        // Override oneLight's hard-coded
                        // `"Fira Code", "Fira Mono", Menlo, ...` chain so
                        // every codeblock uses our loaded JetBrains Mono
                        // and falls through consistently elsewhere. PDF
                        // export embeds the same font for pixel-aligned
                        // text overlay.
                        fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                      }}
                      codeTagProps={{
                        style: {
                          fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                        },
                      }}
                    />
                  </Suspense>
                </div>
              );
            }
            return (
              <code className="cortex-inline-code" {...props}>
                {children}
              </code>
            );
          },
          table({ children, ...props }) {
            return (
              <div className="cortex-table-wrap">
                <table {...props}>{children}</table>
              </div>
            );
          },
          pre({ children }) {
            // Drop the <pre> wrapper that react-markdown auto-emits
            // around fenced code blocks. We replace the inner <code>
            // with <div class="cortex-codeblock"> via the `code`
            // override above; the outer <pre> then carries no
            // semantic or visual purpose, only a browser-default 1em
            // margin we have to fight with a `:has()` selector. That
            // `:has()` rule doesn't reliably re-cascade inside
            // modern-screenshot's <foreignObject> render, so the
            // captured codeblock came out at a different height than
            // the live DOM and broke page-break alignment for every
            // block below it. Stripping the <pre> entirely leaves
            // .cortex-codeblock as the single, structurally-stable
            // wrapper — same shape as .cortex-table-wrap.
            return <>{children}</>;
          },
          video(props: any) {
            // rehypeRaw passes <video> through and React renders it
            // natively. We enforce safe defaults — no autoplay,
            // controls always present, preload="metadata" so the page
            // doesn't pull the whole file just because the tag exists.
            //
            // Sizing: only percent widths are honored (e.g. `width="50%"`).
            // Pixel widths and `height` are dropped because the content
            // column width changes per page-size in PDF export, so
            // absolute px values would be inconsistent. Default render
            // is full content width, centered via `mx-auto`.
            //
            // Print mode: <video> can't be captured to canvas in a
            // meaningful way (modern-screenshot renders it as a black
            // box or empty rect). Substitute a static placeholder so
            // PDF exports of trees containing videos remain useful —
            // poster image (if available), play-button glyph, source
            // filename, and the URL printed as caption.
            const { children, controls, preload, autoPlay, width, height: _height, src, poster, style: rawStyle, ...rest } = props;
            // Width comes from `style="width: N%"` (rehypeRaw passes
            // raw HTML attrs through as React props — `style` is a
            // string here, not the parsed object form). Legacy
            // `width="N%"` attr is also honoured.
            let widthStyle: React.CSSProperties | undefined;
            if (typeof width === "string" && width.endsWith("%")) {
              const n = parseInt(width, 10);
              if (Number.isFinite(n) && n > 0 && n <= 100) {
                widthStyle = { width: `${n}%` };
              }
            }
            if (!widthStyle && typeof rawStyle === "string") {
              const m = /(?:^|;)\s*width:\s*(\d+)%/i.exec(rawStyle);
              if (m) {
                const n = Number(m[1]);
                if (n > 0 && n <= 100) widthStyle = { width: `${n}%` };
              }
            } else if (!widthStyle && rawStyle && typeof rawStyle === "object") {
              const raw = rawStyle as React.CSSProperties;
              const w = raw.width as string | number | undefined;
              if (typeof w === "string" && w.endsWith("%")) {
                const n = parseInt(w, 10);
                if (Number.isFinite(n) && n > 0 && n <= 100) widthStyle = { width: `${n}%` };
              }
            }
            if (printMode) {
              // Static placeholder for PDF capture: poster image when
              // supplied; otherwise a blank slate frame at the same
              // 16:9 aspect ratio so layout is consistent. `<video>`
              // can't render in a captured canvas and the source URL
              // is signed/auth-gated, so we just hold the slot.
              return (
                <figure
                  className="cortex-video-print mx-auto my-4 rounded-md border border-slate-200 overflow-hidden bg-slate-100"
                  style={widthStyle}
                >
                  {poster ? (
                    <img src={rewriteToProxyMediaUrl(poster)} alt="" className="block w-full aspect-video object-cover" />
                  ) : (
                    <div className="aspect-video w-full" />
                  )}
                </figure>
              );
            }
            return (
              <video
                src={src}
                poster={poster}
                controls={controls !== false}
                preload={preload || "metadata"}
                autoPlay={autoPlay ?? false}
                className="cortex-video block mx-auto w-full max-w-full rounded-md border border-slate-200 my-4"
                style={widthStyle}
                {...rest}
              >
                {children}
              </video>
            );
          },
          img({ src, alt, ...props }) {
            // Parse Cortex image-alt hints: "interactive:" (inline SVG) and
            // "<number>:" (percent width). See utils/imageAlt.ts for the
            // full convention. cleanAlt is the user-facing alt with every
            // hint stripped, safe for accessibility.
            const { interactive: isInteractive, widthPercent, cleanAlt } = parseImageAlt(alt);
            const widthStyle = imageWidthStyle(widthPercent);
            // In print mode, swap cross-origin CDN URLs to the same-origin
            // `/api/media/...` proxy so `modern-screenshot` can inline the
            // bytes without tainting the export canvas.
            if (printMode) src = rewriteToProxyMediaUrl(src);

            // Extract filename from various URL formats
            const segments = src ? src.split("/").filter(Boolean) : [];
            const lastSeg = segments[segments.length - 1] || "";
            const secondLast = segments[segments.length - 2] || "";
            const filename = lastSeg.split("?")[0];
            const isChanged = changedAssets && filename && (
              changedAssets.has(filename) || changedAssets.has(secondLast)
            );
            const assetAction = isChanged
              ? (changedAssets!.get(filename) || changedAssets!.get(secondLast) || "modified")
              : null;

            if (isInteractive) {
              return (
                <span className="cortex-inline-svg group relative block" style={widthStyle}>
                  <object
                    type="image/svg+xml"
                    data={src}
                    aria-label={cleanAlt}
                    className="w-full"
                  >
                    <img src={src} alt={cleanAlt} loading="eager" className="cortex-img" />
                  </object>
                  <button
                    onClick={(e) => { e.stopPropagation(); if (src) { setLightboxSrc(src); setLightboxInteractive(true); } }}
                    className="absolute top-2.5 right-2.5 px-1.5 py-1 text-[11px] font-medium rounded-md
                      bg-white/80 text-slate-500 hover:text-slate-700 hover:bg-white
                      border border-slate-200/60 backdrop-blur-sm transition-all
                      opacity-0 group-hover:opacity-100 z-10 flex items-center gap-1"
                    aria-label="Expand diagram"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="10 2 14 2 14 6" />
                      <polyline points="6 14 2 14 2 10" />
                      <line x1="14" y1="2" x2="9.5" y2="6.5" />
                      <line x1="2" y1="14" x2="6.5" y2="9.5" />
                    </svg>
                    Expand
                  </button>
                </span>
              );
            }

            const imgEl = (
              <img
                src={src}
                alt={cleanAlt}
                loading="eager"
                className="cortex-img"
                style={widthStyle}
                onClick={() => src && setLightboxSrc(src)}
                {...props}
              />
            );
            if (isChanged && assetAction) {
              // Only show badge+border for modified assets — added/removed are
              // already inside green/red diff segments so the bg is enough.
              if (assetAction === "modified") {
                return (
                  <span style={{
                    display: "block",
                    position: "relative",
                    border: "2px solid rgb(59, 130, 246)",
                    borderRadius: "8px",
                    padding: "6px",
                    margin: "8px 0",
                  }}>
                    <span style={{
                      position: "absolute",
                      top: 0,
                      left: "14px",
                      transform: "translateY(-50%)",
                      background: "rgb(59, 130, 246)",
                      color: "white",
                      fontSize: "9px",
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: "4px",
                      lineHeight: "1.2",
                      letterSpacing: "0.5px",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}>MOD</span>
                    {imgEl}
                  </span>
                );
              }
              // added/removed — no badge, just render the image normally
              // (the diff segment bg color handles the visual)
            }
            return imgEl;
          },
          a({ href, children, ...props }) {
            // Internal relative links: navigate within the SPA instead of opening new tab
            const isRelative = href && !href.startsWith("http") && !href.startsWith("//") && !href.startsWith("mailto:");
            if (isRelative) {
              // Hash-only links (#anchor) — navigate with search params before hash
              if (href.startsWith("#")) {
                return (
                  <a
                    href={href}
                    onClick={(e) => {
                      if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
                        e.preventDefault();
                        const search = window.location.search;
                        navigate(window.location.pathname + search + href);
                        // Scroll to element after navigation
                        setTimeout(() => {
                          const id = href.slice(1);
                          const el = document.getElementById(id);
                          if (el) el.scrollIntoView({ behavior: "smooth" });
                        }, 50);
                      }
                    }}
                    {...props}
                  >
                    {children}
                  </a>
                );
              }
              let resolvedHref = href;
              if (href.startsWith("/") && !href.startsWith("//")) {
                // Absolute slug-path like /docs/integration/getting-started
                // Prepend the project base: /studio/projects/{slug} or /docs/projects/{slug}
                const loc = window.location.pathname;
                const docsMatch = loc.match(/^(\/docs\/projects\/[^/]+)\//);
                const studioMatch = loc.match(/^(\/studio\/projects\/[^/]+)\//);
                const base = docsMatch?.[1] || studioMatch?.[1] || "";
                if (base) {
                  resolvedHref = base + href;
                }
              } else if (href.startsWith("./") || href.startsWith("../")) {
                // Relative slug link: resolve against current URL with trailing slash
                // so ./chatbot on /docs/projects/portal/docs/products → .../products/chatbot
                const loc = window.location.pathname;
                const base = loc.endsWith("/") ? loc : loc + "/";
                try {
                  resolvedHref = new URL(href, window.location.origin + base).pathname;
                } catch {
                  resolvedHref = href;
                }
              }
              // Cross-section link detection: extract target section slug
              // from resolved path using "projects" anchor (works for any base path)
              const targetParts = resolvedHref.split("/");
              const pIdx = targetParts.indexOf("projects");
              const targetSectionSlug = pIdx >= 0 && targetParts.length > pIdx + 2 ? targetParts[pIdx + 2] : null;
              const isCrossSection = targetSectionSlug && activeSection?.slug && targetSectionSlug !== activeSection.slug;

              if (isCrossSection) {
                return (
                  <a href={resolvedHref} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                );
              }
              return (
                <a
                  href={resolvedHref}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey || e.shiftKey) return;
                    // Caller-supplied handler (e.g. Enable docs embed) wins; if it
                    // handles the link, stop the default browser navigation.
                    if (onNavigate && onNavigate(resolvedHref)) { e.preventDefault(); return; }
                    // Without CmsProvider (diff viewer, review panel), fall
                    // back to default anchor behavior so links still resolve.
                    if (navigateToPath) {
                      e.preventDefault();
                      navigateToPath(resolvedHref);
                    }
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            );
          },
          blockquote({ children, ...props }) {
            return (
              <blockquote className="cortex-callout" {...props}>
                {children}
              </blockquote>
            );
          },
          hr() {
            return <hr className="cortex-divider" />;
          },
          h1({ children, ...props }) {
            const id = slugify(childrenToText(children));
            return <h1 id={id} {...props}>{children}</h1>;
          },
          h2({ children, ...props }) {
            const id = slugify(childrenToText(children));
            return <h2 id={id} {...props}>{children}</h2>;
          },
          h3({ children, ...props }) {
            const id = slugify(childrenToText(children));
            return <h3 id={id} {...props}>{children}</h3>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
    </>
  );
}

// Memoized so re-renders triggered by parent layout/dimension changes
// (e.g. page-tile slicing in the print preview, where the same body
// content gets passed to ~30+ duplicated tile copies) don't re-parse
// markdown when the content itself hasn't changed. Default shallow
// compare on `content` (string), `className` (string), and the
// `changedAssets` Map reference is sufficient — content is the only
// expensive prop, and callers should keep `changedAssets` referentially
// stable when its entries don't change.
const MarkdownRenderer = memo(MarkdownRendererInner);
export default MarkdownRenderer;
